import React from "react";
import styled from "styled-components";
import { GlassButton } from "../styles/GlassComponents";
import { FaCalendarAlt, FaArrowRight } from "react-icons/fa";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import theme from "../styles/theme";

const Wrap = styled.div`
  position: relative; 
  color: ${({theme})=>theme.colors.text.white};
  border-radius: 24px;
  padding: clamp(40px, 5vw, 72px) clamp(32px, 4vw, 56px) clamp(56px, 7vw, 96px) clamp(32px, 4vw, 56px);
  overflow: hidden; 
  box-shadow: 
    0 0 40px rgba(0, 255, 255, 0.6),
    0 0 80px rgba(0, 255, 0, 0.4),
    0 0 120px rgba(255, 0, 128, 0.2),
    inset 0 0 20px rgba(0, 255, 255, 0.1);
  text-align: center;
  margin-top: clamp(70px, 8.7vw, 105px);
  margin-bottom: 48px;
  background: #0D111B;
  border: 2px solid rgba(0, 255, 255, 0.4);
  
  /* Enhanced background effects */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(255, 0, 128, 0.15) 0%, transparent 60%),
      radial-gradient(circle at 80% 20%, rgba(0, 255, 255, 0.15) 0%, transparent 60%),
      radial-gradient(circle at 40% 80%, rgba(0, 255, 0, 0.15) 0%, transparent 60%),
      radial-gradient(circle at 60% 40%, rgba(255, 255, 0, 0.1) 0%, transparent 50%);
    z-index: -1;
  }
  
  /* Animated glow effect */
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, 
      rgba(0, 255, 255, 0.3), 
      rgba(0, 255, 0, 0.3), 
      rgba(255, 0, 128, 0.3), 
      rgba(0, 255, 255, 0.3));
    border-radius: 26px;
    z-index: -2;
    animation: glowRotate 4s linear infinite;
  }
  
  @keyframes glowRotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Spark = styled.div`
  position: absolute; inset: -20% -10% auto auto; width: 420px; height: 420px; border-radius: 50%;
  background: radial-gradient(closest-side, rgba(255,255,255,.22), rgba(255,255,255,0));
  filter: blur(18px); pointer-events: none;
`;

const Title = styled(motion.h1)`
  font-size: clamp(36px, 6vw, 80px);
  line-height: 1.1; 
  font-weight: 900; 
  letter-spacing: .3px; 
  margin: 0 0 16px;
  background: linear-gradient(90deg, #00FF00, #39ff14, #00FF00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 
    0 0 30px rgba(0, 255, 0, 0.75),
    0 0 60px rgba(0, 255, 0, 0.6),
    0 0 90px rgba(0, 255, 0, 0.45);
  font-family: 'Fredoka', 'Arial', sans-serif;
  position: relative;
  z-index: 1;
  
  /* Animated text glow */
  animation: textPulse 2s ease-in-out infinite alternate;
  
  @keyframes textPulse {
    0% { 
      text-shadow: 
        0 0 30px rgba(0, 255, 0, 1),
        0 0 60px rgba(0, 255, 0, 0.8),
        0 0 90px rgba(0, 255, 0, 0.6);
    }
    100% { 
      text-shadow: 
        0 0 40px rgba(0, 255, 0, 1),
        0 0 80px rgba(0, 255, 0, 0.9),
        0 0 120px rgba(0, 255, 0, 0.7);
    }
  }
`;

const Sub = styled(motion.p)`
  font-size: clamp(18px, 2.5vw, 24px); 
  font-weight: 500;
  margin: 0 0 24px;
  color: #ADD8E6;
  text-shadow: 0 0 15px rgba(173, 216, 230, 0.8);
  position: relative;
  z-index: 1;
  
  /* Add bullet points */
  &::before {
    content: '• ';
    color: #00FF00;
    text-shadow: 0 0 10px rgba(0, 255, 0, 0.8);
  }
  
  &::after {
    content: ' •';
    color: #00FF00;
    text-shadow: 0 0 10px rgba(0, 255, 0, 0.8);
  }
`;

const ServicesSection = styled(motion.div)`
  margin: clamp(24px, 4vw, 36px) 0;
  display: flex;
  justify-content: center;
  gap: clamp(8px, 1.5vw, 16px);
  flex-wrap: nowrap;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  overflow-x: auto;
  padding: 0 16px;
  
  @media (max-width: 768px) {
    gap: 6px;
    padding: 0 8px;
  }
  
  @media (max-width: 480px) {
    gap: 4px;
    padding: 0 4px;
  }
`;

const ServiceItem = styled.div`
  background: rgba(57,255,20,.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(57,255,20,.3);
  border-radius: 12px;
  padding: clamp(8px, 1.5vw, 12px) clamp(12px, 2vw, 18px);
  font-weight: 600;
  font-size: clamp(12px, 1.5vw, 14px);
  color: #ffffff;
  transition: all 0.2s ease;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(57,255,20,.2);
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(57,255,20,0.4);
  }
  
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 6px 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
    padding: 5px 8px;
  }
`;

const CTARow = styled.div` 
  display: flex; 
  gap: 12px; 
  flex-wrap: wrap; 
  justify-content: center;
  margin-top: clamp(12px, 2vw, 20px);
  margin-bottom: 16px;
`;

export default function Hero() {
  return (
    <Wrap className="container">
      <Spark />
      <Title initial={{opacity:0, y:16}} animate={{opacity:1, y:0}} transition={{duration:.5}}>
        Bounce Into Fun & Adventure
      </Title>
      <Sub initial={{opacity:0}} animate={{opacity:1}} transition={{delay:.15, duration:.5}}>
        Premium inflatable rentals for every celebration. From bounce houses to water slides, we make your event extraordinary!
      </Sub>
      
      <ServicesSection 
        initial={{opacity:0, y:20}} 
        animate={{opacity:1, y:0}} 
        transition={{delay:.3, duration:.5}}
      >
        <Link to="/products?category=bounce" style={{ textDecoration: 'none', color: 'inherit' }}>
          <ServiceItem className="category-btn">🏠 Bounce Houses</ServiceItem>
        </Link>
        <Link to="/products?category=water" style={{ textDecoration: 'none', color: 'inherit' }}>
          <ServiceItem className="category-btn blue">💧 Water Slides</ServiceItem>
        </Link>
        <Link to="/products?category=obstacle" style={{ textDecoration: 'none', color: 'inherit' }}>
          <ServiceItem className="category-btn pink">❤️ Obstacle Courses</ServiceItem>
        </Link>
        <Link to="/products?category=event" style={{ textDecoration: 'none', color: 'inherit' }}>
          <ServiceItem className="category-btn purple">🎉 Event Rentals</ServiceItem>
        </Link>
      </ServicesSection>
      
      <CTARow>
        <Link to="/products">
          <GlassButton 
            as={motion.button} 
            whileTap={{scale:.98}}
            whileHover={{scale: 1.05, rotate: -2}}
            className="cta-btn"
            style={{
              background: 'linear-gradient(90deg, #00ccff, #00e6e6)',
              color: '#0a0f1c',
              fontFamily: theme.typography.fontFamily.heading,
              fontWeight: 700
            }}
          >
            <FaCalendarAlt /> Explore Rentals
          </GlassButton>
        </Link>
        <Link to="/contact">
          <GlassButton 
            as={motion.button} 
            whileTap={{scale:.98}}
            whileHover={{scale: 1.05, rotate: 2}}
            className="cta-btn green"
            style={{
              background: 'transparent',
              color: '#ffffff',
              border: '2px solid #39ff14',
              fontFamily: theme.typography.fontFamily.heading,
              fontWeight: 700
            }}
          >
            Get a Quote + <FaArrowRight />
          </GlassButton>
        </Link>
      </CTARow>
      
      {/* Ratings section - exactly matching the image */}
      <div className="ratings" style={{
        background: 'rgba(255, 255, 0, 0.1)',
        border: '1px solid #ffff00',
        padding: '1rem',
        borderRadius: '10px',
        margin: '2rem 0',
        color: '#ffff00',
        textAlign: 'center'
      }}>
        ⭐⭐⭐⭐⭐ 4.9/5 from 500+ local families + On-time, spotless, and super friendly!*
      </div>
      
      {/* Perks section - exactly matching the image */}
      <div className="perks" style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '2rem',
        flexWrap: 'wrap',
        margin: '2rem 0'
      }}>
        <span className="free-overnight" style={{color: '#39ff14', fontWeight: 'bold'}}>Free Overnight</span>
        <span className="sanitized" style={{color: '#39ff14', fontWeight: 'bold'}}>Sanitized After Every Rental</span>
        <span className="friendly-crew" style={{color: '#39ff14', fontWeight: 'bold'}}>Friendly Setup Crew</span>
        <span className="fully-insured" style={{color: '#39ff14', fontWeight: 'bold'}}>Fully Insured</span>
      </div>
      
      {/* Divider line - exactly matching the image */}
      <div className="divider" style={{
        height: '2px',
        background: 'linear-gradient(90deg, transparent, #ff0080, transparent)',
        margin: '2rem 0',
        boxShadow: '0 0 10px #ff0080'
      }}></div>
    </Wrap>
  );
}
